<template>
    <div>
        <limitless-page-header :title="getTitle"/>
        <div class="content">
            <b-card no-body>
                <b-card-header>
                    <b-row>
                        <b-col><h5>Sök...</h5></b-col>
                        <b-col class="d-flex justify-content-end">
                            <a :href="app.old_system_url + '/printlabel_history.php'">Till gamla pacsoft arkiv</a></b-col>
                    </b-row>
                    <validation-observer ref="form">
                        <b-row>
                            <b-col>
                                <validation-provider
                                    vid="printer_tag"
                                    name="Skrivare"
                                    v-slot="{ errors }">
                                    <b-form-group
                                        label="Skrivare"
                                        :invalid-feedback="getInvalidFeedback(errors)"
                                        :state="!errors.length">
                                        <label-printer-filter
                                            v-model="filter.printer_tag"
                                            :reduce="opt => opt.tag"
                                            placeholder="-- Filtrera på skrivare --"
                                        />
                                    </b-form-group>
                                </validation-provider>
                                <validation-provider
                                    vid="reference"
                                    name="Referens"
                                    v-slot="{ errors }">
                                    <b-form-group
                                        label="Referens"
                                        :invalid-feedback="getInvalidFeedback(errors)"
                                        :state="!errors.length">
                                        <b-form-input
                                            type="text"
                                            class="form-control"
                                            v-model="filter.reference"
                                            :debounce="debounce"/>
                                    </b-form-group>
                                </validation-provider>
                                <validation-provider
                                    vid="tracking_number"
                                    name="Trackingnummer"
                                    v-slot="{ errors }">
                                    <b-form-group
                                        label="Trackingnummer"
                                        :invalid-feedback="getInvalidFeedback(errors)"
                                        :state="!errors.length">
                                        <b-form-input
                                            type="text"
                                            class="form-control"
                                            v-model="filter.tracking_number"
                                            :debounce="debounce"/>
                                    </b-form-group>
                                </validation-provider>
                            </b-col>
                            <b-col>
                                <validation-provider
                                    vid="receiver.name"
                                    name="Namn"
                                    v-slot="{ errors }">
                                    <b-form-group
                                        label="Namn"
                                        :invalid-feedback="getInvalidFeedback(errors)"
                                        :state="!errors.length">
                                        <b-form-input
                                            type="text"
                                            class="form-control"
                                            v-model="filter.receiver.name"
                                            :debounce="debounce"/>
                                    </b-form-group>
                                </validation-provider>
                                <validation-provider
                                    vid="receiver.address_lines"
                                    name="Adress"
                                    v-slot="{ errors }">
                                    <b-form-group
                                        label="Adress"
                                        :invalid-feedback="getInvalidFeedback(errors)"
                                        :state="!errors.length">
                                        <b-form-input
                                            type="text"
                                            class="form-control"
                                            v-model="filter.receiver.address_lines"
                                            :debounce="debounce"/>
                                    </b-form-group>
                                </validation-provider>
                                <validation-provider
                                    vid="receiver.postal_code"
                                    name="Postnummer"
                                    v-slot="{ errors }">
                                    <b-form-group
                                        label="Postnummer"
                                        :invalid-feedback="getInvalidFeedback(errors)"
                                        :state="!errors.length">
                                        <b-form-input
                                            type="text"
                                            class="form-control"
                                            v-model="filter.receiver.postal_code"
                                            :debounce="debounce"/>
                                    </b-form-group>
                                </validation-provider>
                                <validation-provider
                                    vid="receiver.city"
                                    name="Ort"
                                    v-slot="{ errors }">
                                    <b-form-group
                                        label="Ort"
                                        :invalid-feedback="getInvalidFeedback(errors)"
                                        :state="!errors.length">
                                        <b-form-input
                                            type="text"
                                            class="form-control"
                                            v-model="filter.receiver.city"
                                            :debounce="debounce"/>
                                    </b-form-group>
                                </validation-provider>
                                <validation-provider
                                    vid="receiver.country"
                                    name="Land"
                                    v-slot="{ errors }">
                                    <b-form-group
                                        label="Land"
                                        :invalid-feedback="getInvalidFeedback(errors)"
                                        :state="!errors.length">
                                        <country-filter
                                            v-model="filter.receiver.country"
                                            :reduce="opt => opt.id"
                                            placeholder="-- Filtrera på land --"
                                        />
                                    </b-form-group>
                                </validation-provider>
                            </b-col>
                        </b-row>
                    </validation-observer>
                </b-card-header>
                <b-card-body>
                    <b-alert :show="!isFilterApplied">Visar dom 10 senaste...</b-alert>
                    <b-alert :show="isMaxResults">Visar dom 10 senaste träffarna, förfina sökningen för bättre resultat...</b-alert>
                    <b-table
                        :items="dataProvider"
                        :busy="busy"
                        :fields="fields"
                        :filter="filter"
                    >
                        <template #cell(reference)="{ item }">
                            <b-icon-circle-fill :id="'reference-dot-' + item.id" :class="getReferenceDotClass(item.sender_tag)"></b-icon-circle-fill>
                            <b-tooltip :target="'reference-dot-' + item.id">{{ item.sender_tag }}</b-tooltip>
                            <span v-if="getReferenceLink(item)">
                                <a :href="getReferenceLink(item)">{{ item.reference }}</a>
                            </span>
                            <span v-else>
                                {{ item.reference }}
                            </span>
                        </template>
                        <template #cell(tracking_numbers)="{ item }">
                            <div v-for="(tracking) in item.tracking_numbers" :key="tracking.tracking_number">
                                <span v-if="item.service_type.indexOf('PN-') === 0">
                                    <a :href="'https://www.postnord.se/vara-verktyg/spara-brev-paket-och-pall?shipmentId=' + tracking.tracking_number">{{ tracking.tracking_number }}</a>
                                </span>
                                <span v-else-if="item.service_type.indexOf('UPS-') === 0">
                                    <a :href="'https://www.ups.com/track?loc=en_SE&tracknum=' + tracking.tracking_number + '&requester=ST/trackdetails'">{{ tracking.tracking_number }}</a>
                                </span>
                                <span v-else-if="item.service_type.indexOf('FDX-') === 0">
                                    <a :href="'https://www.fedex.com/fedextrack/?trknbr=' + tracking.tracking_number">{{ tracking.tracking_number }}</a>
                                </span>
                                <span v-else>
                                    {{ tracking.tracking_number }}
                                </span>
                            </div>
                        </template>
                        <template #cell(receiver.address.address_lines)="{ value }">
                            <span v-html="value"></span>
                        </template>
                        <template #cell(status)="{ item: { internal_shipment } }">
                            <div v-if="internal_shipment">
                                <div v-for="parcel in internal_shipment.parcels" :key="parcel.id">
                                    <span :id="'status-' + parcel.id">{{ parcel.statustext }}</span>
                                    <b-tooltip :target="'status-' + parcel.id">{{ parcel.parcel_number }}</b-tooltip>
                                </div>
                            </div>
                        </template>
                        <template #cell(reprint)="{ item }">
                            <a :href="getRePrintLink(item)">
                                <i class="icon-printer"></i>
                            </a>
                        </template>
                        <template #cell(job_id)="{ value }">
                            <a :href="getNtapsLink(value)" target="_blank">[NTAPS]</a>
                        </template>
                    </b-table>
                </b-card-body>
            </b-card>
        </div>
    </div>
</template>
<script>
import {
    formatDateTime,
    hasRole,
} from '@services/Helpers';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader.vue';
import apiClient from '@services/ApiClient';
import LabelPrinterFilter from '@components/Filters/LabelPrinterFilter.vue';
import CountryFilter from '@components/Filters/CountryFilter';
import getError from '@services/ErrorHelper';
import { mapState } from 'pinia';
import { useLayoutHandlerStore } from '@services/pinia/LayoutHandlerStore';
import { BIconCircleFill } from 'bootstrap-vue';

export default {
    components: {
        CountryFilter,
        LabelPrinterFilter,
        LimitlessPageHeader,
        BIconCircleFill,
    },
    data() {
        return {
            printerLabels: {},
            busy: false,
            debounce: 500,
            fields: [
                {
                    key: 'internal_shipment.created_at',
                    label: 'Datum',
                    formatter: formatDateTime,
                },
                {
                    key: 'printer_tag',
                    label: 'Skrivare',
                    formatter: (value) => (this.printerLabels[value] || value),
                },
                {
                    key: 'reference',
                    label: 'Referens',
                },
                {
                    key: 'tracking_numbers',
                    label: 'Trackingnummer',
                },
                {
                    key: 'receiver.address.name',
                    label: 'Namn',
                },
                {
                    key: 'receiver.address.address_lines',
                    label: 'Adress',
                    formatter: (value) => (value.join('<br/>')),
                },
                {
                    key: 'receiver.address.postal_code',
                    label: 'Postnummer',
                },
                {
                    key: 'receiver.address.city',
                    label: 'Ort',
                },
                {
                    key: 'receiver.address.country',
                    label: 'Land',
                },
                {
                    key: 'status',
                    label: 'Status',
                },
                {
                    key: 'reprint',
                    label: 'Skriv ut liknande',
                },
            ],
            filter: {
                printer_tag: null,
                reference: null,
                tracking_number: null,
                receiver: {
                    name: null,
                    address_lines: null,
                    postal_code: null,
                    city: null,
                    country: null,
                },
            },
            resultCount: 0,
        };
    },
    computed: {
        getTitle() {
            return this.$metaInfo?.title;
        },
        isFilterApplied() {
            return this.filter.printer_tag
                || this.filter.reference
                || this.filter.tracking_number
                || this.filter.receiver.name
                || this.filter.receiver.address_lines
                || this.filter.receiver.postal_code
                || this.filter.receiver.city
                || this.filter.receiver.country;
        },
        isMaxResults() {
            return this.isFilterApplied && this.resultCount === 10;
        },
        isAdmin() {
            return this.hasRole('super-admin');
        },
        ...mapState(useLayoutHandlerStore, ['app']),
    },
    created() {
        this.addAdminColumns();
        this.initPrinterNames();
    },
    methods: {
        formatDateTime,
        getError,
        hasRole,
        addAdminColumns() {
            if (this.isAdmin) {
                this.fields.push({ key: 'job_id', label: 'NTAPS' });
            }
        },
        async initPrinterNames() {
            await apiClient.get('/vue/shipping/ntaps/printers')
                .then((response) => {
                    const printers = response.data.map((item) => ([item.tag, item.name]));
                    this.printerLabels = Object.fromEntries(printers);
                });
        },
        async dataProvider(ctx) {
            try {
                const response = await apiClient.post('/vue/shipping/ntaps/shipment/search?with-internal=true', ctx.filter);

                const data = response.data?.data || [];
                this.resultCount = data.length;

                return data;
            } catch (error) {
                const parsedError = getError(error);
                if (typeof parsedError === 'object') {
                    this.$refs.form.setErrors(parsedError);
                }

                return [];
            }
        },
        getInvalidFeedback(errors, key = null) {
            if (key) {
                if (Array.isArray(errors[key]) && errors[key].length) {
                    return errors[key].join(', ');
                }
            }

            if (Array.isArray(errors) && errors.length) {
                return errors.join(', ');
            }

            return null;
        },
        getReferenceLink(item) {
            const { reference } = item;
            let val = reference;

            switch (item.internal_shipment?.internal_reference1) {
            case 1:
                val = `/prodorders/${item.internal_shipment?.internal_reference2}?card_no=${item.internal_shipment?.internal_reference3}`;
                break;
            case 2:
                val = `/returnorders/${item.internal_shipment?.internal_reference2}`;
                break;
            case 3:
                val = `/cardorders/${item.internal_shipment?.internal_reference2}`;
                break;
            default:
                val = null;
                break;
            }

            return val;
        },
        getReferenceDotClass(senderTag) {
            if (senderTag.indexOf('SUPEKORT-') === 0) {
                return 'text-red-700';
            }
            if (senderTag.indexOf('NEWPORT-') === 0) {
                return 'text-blue-700';
            }
            if (senderTag.indexOf('SWEGREEN-') === 0) {
                return 'text-green-700';
            }

            return '';
        },
        getNtapsLink(value) {
            const ntapsHost = this.app.env === 'production' ? 'https://ntaps.newport.int/' : 'https://stage.ntaps.newport.int/';

            return `${ntapsHost}admin/jobs/details/${value}`;
        },
        getRePrintLink(item) {
            const params = JSON.parse(JSON.stringify(item));

            delete params.id;
            delete params.label_zpl;
            delete params.internal_shipment;
            delete params.tos_id;
            delete params.external_id;
            delete params.reference_barcode;
            delete params.sender;
            delete params.service_provider;
            delete params.legal_from;
            delete params.return_to;
            delete params.status;
            delete params.weight;
            delete params.delivery_party;
            delete params.pickup_party;
            delete params.freight_payer;
            delete params.shipment_date;
            delete params.printer_tag;
            delete params.job_id;
            delete params.tracking_numbers;
            delete params.customs;

            params.parcels.forEach((parcel) => {
                delete parcel.id;
                delete parcel.created_at;
                delete parcel.updated_at;
            });

            params.contents = params.contents || { goods: [] };
            params.internal_data = {
                is_reprint: true,
            };

            return '/shipping/print_label?shipment=' + Buffer.from(JSON.stringify(params)).toString('base64');
        },
    },
};
</script>
